import { coreClient, apiRequest } from '../client.js';
import type {
  DungeonCharacterListParams,
  DungeonCharacterListResponse,
  DungeonCharacterRecord,
  DungeonCharacterUpsert,
  DungeonSessionListParams,
  DungeonSessionListResponse,
  DungeonSessionDetails,
  DungeonSessionEventsResponse,
  DungeonEventParams,
  DungeonMetrics,
} from '../types.js';

export type DungeonSessionDeletePreview = {
  results: Array<{
    sessionId: string;
    status: 'ok' | 'missing_session' | 'already_deleted' | 'missing_thread' | 'discord_missing' | 'discord_error' | 'discord_unavailable';
    error?: string;
  }>;
  summary: {
    requested: number;
    missingSessions: number;
    alreadyDeleted: number;
    missingThread: number;
    discordMissing: number;
    discordError: number;
    discordUnavailable: number;
    ok: number;
    mismatchCount: number;
    discordAvailable: boolean;
  };
};

export type DungeonSessionBulkDeleteResult = {
  results: Array<{
    sessionId: string;
    status:
      | 'missing_session'
      | 'already_deleted'
      | 'deleted'
      | 'deleted_ui_only'
      | 'deleted_ui_only_missing_thread'
      | 'deleted_ui_only_discord_unavailable'
      | 'deleted_ui_only_discord_error'
      | 'skipped_missing_thread'
      | 'skipped_discord_unavailable'
      | 'skipped_discord_missing'
      | 'skipped_discord_error';
    error?: string;
  }>;
  summary: {
    requested: number;
    deleted: number;
    deletedUiOnly: number;
    skipped: number;
    missing: number;
    alreadyDeleted: number;
  };
};

export const dungeonApi = {
  listCharacters: (params?: DungeonCharacterListParams) =>
    apiRequest<DungeonCharacterListResponse>(coreClient, {
      method: 'GET',
      url: '/dungeon/characters',
      params,
    }),

  upsertCharacter: (userId: string, payload: DungeonCharacterUpsert) =>
    apiRequest<DungeonCharacterRecord>(coreClient, {
      method: 'PUT',
      url: `/dungeon/characters/${userId}`,
      data: payload,
    }),

  deleteCharacter: async (characterId: string) => {
    await apiRequest(coreClient, {
      method: 'DELETE',
      url: `/dungeon/characters/${characterId}`,
    });
    return { success: true } as const;
  },

  getServerSettings: (guildId: string) =>
    apiRequest<{
      guildId: string;
      serverId: string;
      dungeonSettings: { allowedChannelId?: string | null };
    }>(coreClient, {
      method: 'GET',
      url: `/dungeon/servers/${guildId}/settings`,
    }),

  updateDungeonSettings: (guildId: string, settings: { allowedChannelId?: string | null }) =>
    apiRequest<{ guildId: string; serverId: string; dungeonSettings: { allowedChannelId?: string | null } }>(coreClient, {
      method: 'PUT',
      url: `/dungeon/servers/${guildId}/settings/dungeon`,
      data: settings,
    }),

  getSummarySettings: (guildId: string) =>
    apiRequest<{ guildId: string | null; summaryRounds: number; source: 'guild' | 'global' | 'default' }>(coreClient, {
      method: 'GET',
      url: `/dungeon/settings/${guildId}`,
    }),

  updateSummarySettings: (guildId: string, settings: { summaryRounds: number }) =>
    apiRequest<{ guildId: string | null; summaryRounds: number; source: 'guild' | 'global' }>(coreClient, {
      method: 'PUT',
      url: `/dungeon/settings/${guildId}`,
      data: settings,
    }),
};

export const dungeonUiApi = {
  listSessions: (params?: DungeonSessionListParams) =>
    apiRequest<DungeonSessionListResponse>(coreClient, {
      method: 'GET',
      url: '/dungeon/ui/sessions',
      params,
    }),

  getSessionDetails: (sessionId: string) =>
    apiRequest<DungeonSessionDetails>(coreClient, {
      method: 'GET',
      url: `/dungeon/ui/sessions/${sessionId}/details`,
    }),

  getSessionEvents: (sessionId: string, params?: DungeonEventParams) =>
    apiRequest<DungeonSessionEventsResponse>(coreClient, {
      method: 'GET',
      url: `/dungeon/ui/sessions/${sessionId}/events`,
      params,
    }),

  getMetrics: () =>
    apiRequest<DungeonMetrics>(coreClient, {
      method: 'GET',
      url: '/dungeon/ui/metrics',
    }),

  deleteSession: (sessionId: string) =>
    apiRequest<{ success: boolean; message: string }>(coreClient, {
      method: 'PATCH',
      url: `/dungeon/ui/sessions/${sessionId}/delete`,
    }),

  cleanupOrphanedSessions: () =>
    apiRequest<{ success: boolean; deletedCount: number; message: string }>(coreClient, {
      method: 'PATCH',
      url: '/dungeon/ui/sessions/cleanup',
    }),

  previewBulkDeleteSessions: (sessionIds: string[]) =>
    apiRequest<DungeonSessionDeletePreview>(coreClient, {
      method: 'POST',
      url: '/dungeon/ui/sessions/bulk-delete/preview',
      data: { sessionIds },
    }),

  bulkDeleteSessions: (payload: { sessionIds: string[]; deleteDiscord?: boolean; forceUiDelete?: boolean }) =>
    apiRequest<DungeonSessionBulkDeleteResult>(coreClient, {
      method: 'POST',
      url: '/dungeon/ui/sessions/bulk-delete',
      data: payload,
    }),
};

export default dungeonApi;
